import { UnknownMethodError } from '../../errors'
import type { ExecutionContext } from '../context/execution-context'
import type { Extension } from '../extension'
import type { LoaderOwner } from '../loaders/loader-owner'
import { ExtensionMethod } from '../method'
import type { SharedExtensionMethod } from '../model/model-types'
import { toArgs, toBodyParams } from '../model/params-model'
import { encodeParams, MethodParams } from '../params'
import { ResultSpec } from '../result'
import { PollingMechanism } from './mechanisms'
import type { PollingInvocation, PollingResult, PollingTriggerProps } from './types'

// A polling trigger (08-polling-triggers.md). It is a published method — the console renders it as a start
// node with its own params and sample result — but it is NOT invocable by name: the runtime reaches it
// through the shared polling SYSTEM method, which routes by `eventName` (see ./polling-triggers.ts).

export class PollingTriggerMethod extends ExtensionMethod implements LoaderOwner {
  readonly params: MethodParams
  readonly result: ResultSpec
  readonly mechanism: PollingMechanism

  constructor(extension: Extension, private readonly props: PollingTriggerProps) {
    super(extension)

    this.params = MethodParams.of(props.params, {
      serviceId: extension.id,
      kind: 'polling trigger',
      itemId: props.id,
      slot: 'params',
    })
    this.result = ResultSpec.from(props.result)
    // Validates the declared combination — see PollingMechanism.from.
    this.mechanism = PollingMechanism.from(props, extension.id)
  }

  get id(): string {
    return this.props.id
  }

  get name(): string {
    return this.props.id
  }

  get registerAs(): string {
    return 'POLLING_TRIGGER'
  }

  get category(): string {
    return this.props.category
  }

  get oauth2Scopes(): string[] {
    return this.props.oauth2Scopes ?? []
  }

  // ── runtime ────────────────────────────────────────────────────────────────

  // Not dispatchable by name — the platform ticks `handleTriggerPollingForEvent` instead, so a call that
  // lands here is a caller using the trigger id as if it were an action.
  execute(): never {
    throw new UnknownMethodError(this.extension.id, this.name)
  }

  // One tick for this trigger, with its instance params already validated.
  poll(invocation: PollingInvocation, ctx: ExecutionContext): Promise<PollingResult> {
    return this.mechanism.run(
      { ...invocation, triggerData: this.#triggerData(invocation.triggerData) },
      ctx.toBag()
    )
  }

  // ── output model ───────────────────────────────────────────────────────────

  toModel(): SharedExtensionMethod {
    const [ownStart, ownEnd] = this.props.appearanceColors ?? []
    const [colorStart, colorEnd] = this.extension.appearanceColors

    return {
      name: this.id,
      operationName: this.props.label,
      description: this.props.description || '',
      registerAs: this.registerAs,
      category: this.category,
      colorStart: ownStart ?? colorStart,
      colorEnd: ownEnd ?? colorEnd,
      customIcon: this.props.logo ?? '',
      returnType: this.result.returnType,
      queryParams: [],
      bodyParams: toBodyParams(this.params),
      paramsSchema: encodeParams(this.params.scheme),
      metaInfo: { args: toArgs(this.params), ...this.result.toMetaInfo(this.id, this.params) },
    }
  }

  // A trigger instance's configured params, through the same pipeline as everything else. A saved instance
  // missing a required param therefore fails the poll rather than polling with `undefined` — the trigger
  // genuinely cannot work without it. Unknown fields ride through to the handler.
  #triggerData(data: Record<string, unknown> = {}): Record<string, unknown> {
    return this.params.normalize(data, this.input(data).invalidInput('triggerData'), { carryUnknown: true })
  }
}
